import { Injectable } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { RequiredValidator } from '../customValidators/required/required';
import { Regex } from '../dataModels/regex';
import { LOV_KEYS, HRCHY_KEYS } from '../dataModels/lovs';
import { CONSTANTS } from '../dataModels/constants';
import { ResourceManagerService } from './resourceManager/resource-manager.service';
import { HelperService } from './utils/helper.service';
import { GlobalService } from '../pages/global/global.service';

/**
 * service for address controls
 */
@Injectable({
  providedIn: 'root',
})
export class AddressService {
  /**
   * list of states
   */
  stateList = [];

  /**
   * list of county Municipalities
   */
  countyMuniList = [];

  /**
   * constructor to inject dependency
   * @param resourceService instance of resourceManagerService
   * @param helperService instance of HelperService
   * @param globalService instance of globalService
   */
  constructor(
    private resourceService: ResourceManagerService,
    private helperService: HelperService,
    private globalService: GlobalService
  ) {}

  /**
   * get address line controls
   * @param addressLineMaxLength maxlength for addressline1 and addressline2 fields, if nothing is specified defaults to 50
   * @param addressLineRegex regex for addressline1 and addressline 2 fields, if nothing is specified defaults to Regex.GENERAL pattern
   * @returns address form controls
   */
  getAddressControls(addressLineMaxLength = 50, addressLineRegex = Regex.GENERAL) {
    return {
      addressLine1: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.minLength(3),
        Validators.maxLength(addressLineMaxLength),
        Validators.pattern(addressLineRegex),
      ]),
      addressLine2: new UntypedFormControl('', [
        Validators.minLength(3),
        Validators.maxLength(addressLineMaxLength),
        Validators.pattern(addressLineRegex),
      ]),
      city: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.minLength(3),
        Validators.maxLength(34),
        Validators.pattern(Regex.ALPHA),
      ]),
      state: new UntypedFormControl('', [Validators.required]),
      zip: new UntypedFormControl('', [Validators.required]),
      countyCode: new UntypedFormControl('', [Validators.required]),
      municipalityCode: new UntypedFormControl('', [Validators.required]),
    };
  }

   /**
   * get address line controls
   * @param addressLineMaxLength maxlength for addressline1 and addressline2 fields, if nothing is specified defaults to 50
   * @param addressLineRegex regex for addressline1 and addressline 2 fields, if nothing is specified defaults to Regex.GENERAL pattern
   * @returns address form controls
   */
   getPhyAddressControls(addressLineMaxLength = 50, addressLineRegex = Regex.GENERAL) {
    return {
      phyAddressLine1: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.minLength(3),
        Validators.maxLength(addressLineMaxLength),
        Validators.pattern(addressLineRegex),
      ]),
      phyAddressLine2: new UntypedFormControl('', [
        Validators.minLength(3),
        Validators.maxLength(addressLineMaxLength),
        Validators.pattern(addressLineRegex),
      ]),
      phyCity: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.minLength(3),
        Validators.maxLength(34),
        Validators.pattern(Regex.ALPHA),
      ]),
      phyState: new UntypedFormControl('', [Validators.required]),
      phyZip: new UntypedFormControl('', [Validators.required]),
      phyCountyCode: new UntypedFormControl('', [Validators.required]),
      phyMunicipalityCode: new UntypedFormControl('', [Validators.required]),
    };
  }

  /**
   * get lovs for address
   * 1. LOV_KEYS.STATE
   * 2. HRCHY_KEYS.COUNTY_MUNI
   */
  async getLovs() {
    const resourceList = [
      {
        key: LOV_KEYS.STATE,
        type: CONSTANTS.LOV,
      },
      {
        key: HRCHY_KEYS.COUNTY_MUNI,
        type: CONSTANTS.HRCHY,
      },
    ];

    if (this.helperService.isUserLoggedIn) {
      await this.resourceService.getResources(resourceList).then((response) => {
        this.stateList = response[LOV_KEYS.STATE];
        this.countyMuniList = this.helperService.getActiveCountyAndMunicipalityList(response[HRCHY_KEYS.COUNTY_MUNI]);
      });
    } else {
      await this.globalService.getResources(resourceList).then((response) => {
        this.stateList = response[LOV_KEYS.STATE];
        this.countyMuniList = this.helperService.getActiveCountyAndMunicipalityList(response[HRCHY_KEYS.COUNTY_MUNI]);
      });
    }

    return {
      stateList: this.stateList,
      countyMuniList: this.countyMuniList,
    };
  }

  /**
   * On state change, enable, disable county, municipality controls
   * 1. If state is not NJ, clear County and Municipality and disable County and Municipality
   * 2. If State is NJ, clear County and Municipality and enable County and Municipality
   * @param frm formGroup
   */
  stateChange(frm) {
    const selectedState = frm.getRawValue().state;

    let stateDescr = '';
    let countyMuniValue = '';
    let isDisabled = false;

    if (!this.helperService.IsNullOrEmpty(selectedState)) {
      if (selectedState !== 'NJ') {
        countyMuniValue = '0000';
        isDisabled = true;
      }
      //isDisabled = false;
      stateDescr = this.resourceService.getLovColumnProperties(LOV_KEYS.STATE, selectedState, 'descr');
    } else {
      isDisabled = true;
    }

    this.modifyCountyMuniControls(frm, countyMuniValue, isDisabled);

    return {
      stateDescr,
      countyMuniValue,
      isDisabled,
    };
  }
  
  /**
   * On state change, enable, disable county, municipality controls
   * 1. If state is not NJ, clear County and Municipality and disable County and Municipality
   * 2. If State is NJ, clear County and Municipality and enable County and Municipality
   * @param frm formGroup
   */
  phyStateChange(frm) {
    const selectedState = frm.getRawValue().phyState;

    let stateDescr = '';
    let countyMuniValue = '';
    let isDisabled = false;

    if (!this.helperService.IsNullOrEmpty(selectedState)) {
      if (selectedState !== 'NJ') {
        countyMuniValue = '0000';
        isDisabled = true;
      }
      //isDisabled = false;
      stateDescr = this.resourceService.getLovColumnProperties(LOV_KEYS.STATE, selectedState, 'descr');
    } else {
      isDisabled = true;
    }

    this.phyModifyCountyMuniControls(frm, countyMuniValue, isDisabled);

    return {
      stateDescr,
      countyMuniValue,
      isDisabled,
    };
  }

  stateChangeform(frm, stateList) {
    const selectedState = frm.getRawValue().state;
    let stateDescr = '';
    let countyMuniValue = '';
    let isDisabled = false;

    if (!this.helperService.IsNullOrEmpty(selectedState)) {
      if (selectedState !== 'NJ') {
        countyMuniValue = '0000';
        isDisabled = true;
      }
      //isDisabled = false;
      stateDescr = this.resourceService.getLovColumnPropertiesForm(LOV_KEYS.STATE, selectedState, 'descr', stateList);
    } else {
      isDisabled = true;
      stateDescr = this.resourceService.getLovColumnPropertiesForm(LOV_KEYS.STATE, selectedState, 'descr', stateList);
      // stateDescr = getLovColumnProperties(LOV_KEYS.STATE, selectedState, 'descr', 'columnValue') {
      //   const stateresources = stateList;
      //   console.log({resources340service: stateList})
      //   let result = '';
      //   if (stateresources) {
      //     const lovList = this.stateList;
    
      //     result = this.getLovProperty(lovList, propertyToCheck, lovColumnVal, propertyToReturn);
      //   } else {
      //     result = '';
      //   }
      //   return result;
      // }
    }

    this.modifyCountyMuniControls(frm, countyMuniValue, isDisabled);

    return {
      stateDescr,
      countyMuniValue,
      isDisabled,
    };
  }
  /**
   * On county change, re-load Municipality List with selected county
   * @param frm formGroup
   */
  countyChange(frm) {
    let municipalityList = [];

    let selectedCountyCode = frm.getRawValue().countyCode || '0000';

    if (selectedCountyCode.length === 2) {
      selectedCountyCode = `${selectedCountyCode}00`;
    }

    const matchedCounty = this.countyMuniList?.find((county) => county.countyCode === selectedCountyCode);

    const countyDescr = matchedCounty?.countyName;

    if (matchedCounty) {
      municipalityList = matchedCounty.municipality;
    }
    return {
      countyDescr,
      municipalityList,
    };
  }

    /**
   * On county change, re-load Municipality List with selected county
   * @param frm formGroup
   */
  phyCountyChange(frm) {
    let municipalityList = [];
  
    let selectedCountyCode = frm.getRawValue().phyCountyCode || '0000';
  
    if (selectedCountyCode.length === 2) {
      selectedCountyCode = `${selectedCountyCode}00`;
    }
  
    const matchedCounty = this.countyMuniList?.find((county) => county.countyCode === selectedCountyCode);
  
    const countyDescr = matchedCounty?.countyName;
  
    if (matchedCounty) {
      municipalityList = matchedCounty.municipality;
    }
    return {
      countyDescr,
      municipalityList,
    };
  }

  facCountyChange(facCountyCode) {
    let municipalityList = [];

    let selectedCountyCode = facCountyCode || '0000';

    if (selectedCountyCode.length === 2) {
      selectedCountyCode = `${selectedCountyCode}00`;
    }

    const matchedCounty = this.countyMuniList?.find((county) => county.countyCode === selectedCountyCode);

    const countyDescr = matchedCounty?.countyName;

    if (matchedCounty) {
      municipalityList = matchedCounty.municipality;
    }
    return {
      countyDescr,
      municipalityList,
    };
  }

  countyUpdate(frm, countyMuniList) {
    console.log({countyMuniList})
    let municipalityList = [];
    let selectedCountyCode = frm.getRawValue().countyCode || '0000';

    if (selectedCountyCode.length === 2) {
      selectedCountyCode = `${selectedCountyCode}00`;
    }

    const matchedCounty = countyMuniList?.find((county) => county.countyCode === selectedCountyCode);
    console.log({matchedcounty: matchedCounty})

    const countyDescr = matchedCounty?.countyName;
    console.log({nameofcounty: countyDescr})

    if (matchedCounty) {
      municipalityList = matchedCounty.municipality;
    }
    return {
      countyDescr,
      municipalityList,
    };
  }

  phycountyUpdate(frm, countyMuniList) {
    console.log({countyMuniList})
    let municipalityList = [];
    let selectedCountyCode = frm.getRawValue().phyCountyCode || '0000';

    if (selectedCountyCode.length === 2) {
      selectedCountyCode = `${selectedCountyCode}00`;
    }

    const matchedCounty = countyMuniList?.find((county) => county.countyCode === selectedCountyCode);
    console.log({matchedcounty: matchedCounty})

    const countyDescr = matchedCounty?.countyName;
    console.log({nameofcounty: countyDescr})

    if (matchedCounty) {
      municipalityList = matchedCounty.municipality;
    }
    return {
      countyDescr,
      municipalityList,
    };
  }

  countyUserUpdate(frm, countyMuniList) {
   // console.log("fm", frm);
    //console.log({countyMuniList})
    let municipalityList = [];
    let selectedCountyCode = frm.countyCode || '0000';

    if (selectedCountyCode.length === 2) {
      selectedCountyCode = `${selectedCountyCode}00`;
    }

    const matchedCounty = countyMuniList?.find((county) => county.countyCode === selectedCountyCode);
    console.log({matchedcounty: matchedCounty})

    const countyDescr = matchedCounty?.countyName;
    console.log({nameofcounty: countyDescr})

    if (matchedCounty) {
      municipalityList = matchedCounty.municipality;
    }
    return {
      countyDescr,
      municipalityList,
    };
  }
  /**
   * Set values and enable/disable County and Municipality
   * @param frm formGroup
   * @param value value to set to County and Municipality
   * @param isDisable to enable or disable County and Municipality
   */
  modifyCountyMuniControls(frm, value, isDisable) {
    frm.controls.countyCode.setValue(value);
    frm.controls.municipalityCode.setValue(value);
    if (isDisable) {
      frm.controls.countyCode.disable();
      frm.controls.municipalityCode.disable();
    } else {
      frm.controls.countyCode.enable();
      frm.controls.municipalityCode.enable();
    }
  }

  /**
   * Set values and enable/disable County and Municipality
   * @param frm formGroup
   * @param value value to set to County and Municipality
   * @param isDisable to enable or disable County and Municipality
   */
  phyModifyCountyMuniControls(frm, value, isDisable) {
    frm.controls.phyCountyCode.setValue(value);
    frm.controls.phyMunicipalityCode.setValue(value);
    if (isDisable) {
      frm.controls.phyCountyCode.disable();
      frm.controls.phyMunicipalityCode.disable();
    } else {
      frm.controls.phyCountyCode.enable();
      frm.controls.phyMunicipalityCode.enable();
    }
  }

  /**
   * check if municipalityCode being set to the model is present in the list and return municipality code else return empty string
   * @param municipalityList list of municipality list
   * @param municipalityCode municipalityCode to set to the model
   * @returns municipalityCode if municipalityCode is present in the municipalityList, else return empty string
   */
  getMunicipality(municipalityList, municipalityCode) {
    const municipality = municipalityList.find((x) => x.municipalityCode === municipalityCode);

    return municipality ? municipalityCode : '';
  }

  // getFacilitySubType(facilitySubTypeList, faciltySubTypeCode) {
  //   return "text"
  // }
}
